import React, { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  BoundDatasetsObjectMapping,
  Maybe,
  MdmObject,
  MdmObjectDto,
  useDeleteObjectMutation,
  useGetCoordinatesLazyQuery,
  useGetProjectQuery
} from "gql/generated/types";
import { Badge } from 'primereact/badge';
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabPanelHeaderTemplateOptions, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { getFormErrorMessage } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import AdditionalInfoEdit from "./AdditionalInfoEdit";
import DatasetsTab from "./DatasetsTab";
import ObjectAnalytics from "./ObjectAnalytics";
import ObjectDevices from "./ObjectDevices";
import ObjectJobs from "./ObjectJobs";
import ObjectNotes from "./ObjectNotes";

type ObjectFormProps = {
  object: MdmObjectDto | null;
  onSubmit: (object: MdmObjectDto) => void;
  loading?: boolean;
};

const emptyObject: Partial<MdmObjectDto> = {
  tags: [],
};

const schema = z.object({
  title: z.string().min(1, "Required"),
});

const ObjectForm: React.FC<ObjectFormProps> = ({ object, onSubmit, loading }) => {
  const [objectState, setObjectState] = useState<MdmObjectDto>(object ?? emptyObject as MdmObjectDto);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const { projectId } = useParams();

  const { data: projectData } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });
  const objectAttributes = projectData?.mdmProjectById?.configuration?.objectConfiguration.customFields ?? [];

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const [deleteObjectMutation, { loading: loadingDelete }] = useDeleteObjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Object deleted successfully",
        life: 3000,
      });
      navigate(`/mdm/${projectId}/`);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const [getCoordinates, { data, loading: loadingCoord, error }] = useGetCoordinatesLazyQuery();

  useEffect(() => {
    if (data && data.coordinates) {
      setObjectState(prev => ({
        ...prev,
        location: {
          ...prev.location,
          latitude: data.coordinates[0]?.latitude,
          longitude: data.coordinates[0]?.longitude,
        },
      }));
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, address: { ...prev.address, [name]: value } } as MdmObjectDto));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this object?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (object) {
          deleteObjectMutation({
            variables: { id: object.id! },
            update(cache) {
              const normalizedId = cache.identify({ id: object.id, __typename: "MdmObjectDTO" });
              cache.evict({ id: normalizedId });
              cache.gc();
            },
          });
        }
      },
    });
  };

  const renderTitleControl = (field: ControllerRenderProps<Partial<MdmObject>, any>) => (
    <InputLabelWrapper labelName="Object Name" col={4}>
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="title"
        placeholder="Object Name"
      />
      {getFormErrorMessage("title", errors)}
    </InputLabelWrapper>
  );

  const tabHeaderTemplate = (options: TabPanelHeaderTemplateOptions, count: number) => (
    <div
      className={`p-tabview-nav-link${count === 0 ? "" : " p-3"}`}
      onClick={options.onClick}
      role="tab"
      tabIndex={0}
    >
      <span className="p-tabview-title">{options.titleElement}</span>
      {count !== 0 &&<Badge value={count} className="ml-2" />}
    </div>
  );

  return (
    <div className="flex justify-content-between flex-column gap-5 mx-auto">
      <ConfirmPopup />
      {!editMode && object && (
        <div>
          <div className="text-right">
            {object?.location && (
              <Button
                type="button"
                icon="pi pi-map"
                title="Show on map"
                className="p-button-rounded p-button-text"
                onClick={() => navigate(`/mdm/${projectId}/map/${object?.id}`)}
              />
            )}
            <Button
              onClick={() => setEditMode(true)}
              type="button"
              title="Edit"
              className="p-button-rounded p-button-text p-button-sm"
              icon="pi pi-pencil"
            />
          </div>

          <div className="info-table-wrapper">
            <table className="info-table">
              <caption>Object Details</caption>
              <tbody>
                <tr>
                  <th>Identifier:</th>
                  <td>{object?.identifier ?? "-"}</td>
                </tr>
                <tr>
                  <th>Title:</th>
                  <td>{object?.title ?? "-"}</td>
                </tr>
                <tr>
                  <th>Description:</th>
                  <td>{object?.description ?? "-"}</td>
                </tr>
                <tr>
                  <th>Address:</th>
                  <td>{object?.address?.fullAddress ?? "-"}</td>
                </tr>
                <tr>
                  <th>Latitude:</th>
                  <td>{object?.location?.latitude?.toFixed(5) ?? "-"}</td>
                </tr>
                <tr>
                  <th>Longitude:</th>
                  <td>{object?.location?.longitude?.toFixed(5) ?? "-"}</td>
                </tr>
                <tr>
                  <th>Tags:</th>
                  <td>{object?.tags?.length ? object?.tags?.join(", ") : "-"}</td>
                </tr>
              </tbody>
            </table>
            {object && objectAttributes.length > 0 && (
              <AdditionalInfo attributes={objectAttributes} objectState={objectState} />
            )}
          </div>
        </div>
      )}
      {(editMode || !object) && (
        <form>
          <div className="grid p-fluid">
            <InputLabelWrapper labelName="Identifier" col={2}>
              <InputText
                value={objectState.identifier ?? ""}
                onChange={handleChange}
                className="p-inputtext-sm"
                name="identifier"
                placeholder="Identifier"
              />
            </InputLabelWrapper>
            <Controller
              defaultValue={object?.title ?? ""}
              control={control}
              name="title"
              render={({ field }) => renderTitleControl(field)}
            />
            <InputLabelWrapper labelName="Description">
              <InputText
                value={objectState.description ?? ""}
                onChange={handleChange}
                className="p-inputtext-sm"
                name="description"
                placeholder="Description"
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="Country" col={1}>
              <InputText
                value={objectState.address?.country ?? ""}
                onChange={handleAddressChange}
                className="p-inputtext-sm"
                name="country"
                placeholder="Country"
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="City" col={1}>
              <InputText
                value={objectState.address?.city ?? ""}
                onChange={handleAddressChange}
                className="p-inputtext-sm"
                name="city"
                placeholder="City"
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="Street" col={2}>
              <InputText
                value={objectState.address?.street ?? ""}
                onChange={handleAddressChange}
                className="p-inputtext-sm"
                name="street"
                placeholder="Street"
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="House" col={1}>
              <InputText
                value={objectState.address?.houseNumber ?? ""}
                onChange={handleAddressChange}
                className="p-inputtext-sm"
                name="houseNumber"
                placeholder="House number"
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="Post code" col={1}>
              <InputText
                value={objectState.address?.postCode ?? ""}
                onChange={handleAddressChange}
                className="p-inputtext-sm"
                name="postCode"
                placeholder="Post code"
              />
            </InputLabelWrapper>
            <div className="col-2"/>
            <InputLabelWrapper labelName="Latitude/Longitude" col={4}>
              <div className="p-inputgroup">
                <Button
                  type="button"
                  loading={loadingCoord}
                  label="Get coordinates"
                  onClick={() => {
                    const { fullAddress, ...address } = objectState.address!;
                    getCoordinates({
                      variables: { address },
                    });
                  }}
                />
                <InputNumber
                  value={objectState.location?.latitude}
                  onChange={e =>
                    setObjectState(prev => ({
                      ...prev,
                      location: { ...prev.location, latitude: e.value },
                    }))
                  }
                  className="p-inputtext-sm"
                  name="latitude"
                  placeholder="Latitude"
                  useGrouping={false}
                  maxFractionDigits={10}
                />
                <InputNumber
                  value={objectState.location?.longitude}
                  onChange={e =>
                    setObjectState(prev => ({
                      ...prev,
                      location: { ...prev.location, longitude: e.value },
                    }))
                  }
                  className="p-inputtext-sm"
                  name="longitude"
                  placeholder="Longitude"
                  useGrouping={false}
                  maxFractionDigits={10}
                />
              </div>
            </InputLabelWrapper>
            {error && (
              <div className="flex align-items-end col-6 text-red-500">{error.message}</div>
            )}
            <InputLabelWrapper labelName="Tags">
              <Chips
                value={objectState.tags ?? []}
                onChange={handleChange}
                name="tags"
                placeholder="Tags.."
              />
            </InputLabelWrapper>
            {object && objectAttributes.length > 0 && (
              <AdditionalInfoEdit
                attributes={objectAttributes}
                objectState={objectState}
                setObjectState={setObjectState}
                editMode={false}
              />
            )}
          </div>
          <div className="flex w-full justify-content-end gap-3 mt-3">
            <Button
              type="submit"
              onClick={handleSubmit(() => {
                onSubmit(objectState);
                setEditMode(false);
              })}
              label={object ? "Update" : "Create"}
              loading={loading}
              className={`p-button-rounded p-button-${object ? "primary" : "success"} w-10rem`}
            />
            {object && (
              <>
                <Button
                  type="button"
                  label="Delete"
                  className="p-button-rounded p-button-danger w-10rem"
                  onClick={handleDelete}
                  loading={loadingDelete}
                />
                <Button
                  type="button"
                  onClick={() => {
                    setObjectState(object ?? emptyObject);
                    setEditMode(false);
                  }}
                  label="Cancel"
                  className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
                />
              </>
            )}
            {!object && (
              <Button
                type="button"
                onClick={() => navigate(-1)}
                label="Back"
                className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
              />
            )}
          </div>
        </form>
      )}

      {object && (
        <TabView className="col-12">
          <TabPanel header="Devices">
            <ObjectDevices object={object} />
          </TabPanel>
          <TabPanel header="Datasets">
            <ObjectDatasets object={object} />
          </TabPanel>
          <TabPanel header="Analytics">
            <ObjectAnalytics object={object} tableName="AnalyticsByObject" />
          </TabPanel>
          <TabPanel
            header="Notes"
            headerTemplate={options => tabHeaderTemplate(options, object.info.notesCount)}
          >
            <ObjectNotes object={object} />
          </TabPanel>
          <TabPanel
            header="Jobs"
            headerTemplate={options => tabHeaderTemplate(options, object.info.jobsCount)}
          >
            <ObjectJobs object={object} tableName="JobsByObject" />
          </TabPanel>
        </TabView>
      )}
    </div>
  );
};

export default ObjectForm;

const ObjectDatasets: React.FC<{ object: MdmObjectDto }> = ({object}) => {
  const options = [
    { title: "All Datasets", data: object.boundDatasets.all },
    { title: "Object Datasets", data: object.boundDatasets.direct },
    { title: "Devices Datasets", data: object.boundDatasets.indirect },
  ];

  const [datasets, setDatasets] = useState<{
    title: string;
    data: Maybe<BoundDatasetsObjectMapping>[];
  }>(options[1]);

  return (
    <div className="flex flex-column align-items-start">
      <Dropdown
        value={datasets}
        options={options}
        optionLabel="title"
        onChange={e => setDatasets(e.value)}
        className="ml-3"
      />
      <DatasetsTab datasets={datasets.data?.map(d => d?.dataset!)} />
    </div>
  );
};
